import {
  Bot,
  MessageCircle,
  AudioLines,
  Workflow,
  Sparkles,
  Globe,
  LayoutPanelLeft,
  Smartphone,
  ServerCog,
  TrendingUp,
  Share2,
  RefreshCw,
  BrainCircuit,
  Zap,
  HeartHandshake,
  Scaling,
  UtensilsCrossed,
  ShoppingBag,
  Briefcase,
  Rocket,
  Building2,
  Puzzle,
  Target,
  Cpu,
  Leaf,
  MapPin,
} from "lucide-react";

// ---------------------------------------------------------------------------
// PLACEHOLDER CONTACT DETAILS — replace email/phone/address with the official
// company details when ready. Everything reads from this one object.
// ---------------------------------------------------------------------------
export const COMPANY = {
  name: "Prata Technologies Ltd",
  brand: "Prata Technologies",
  website: "pratatechnologiesltd.com",
  websiteUrl: "https://pratatechnologiesltd.com",
  companyNumber: "17336053",
  email: "info@pratatechnologiesltd.com", // placeholder — confirm official email
  phone: "+44 (0)20 7946 0000", // placeholder — replace with business phone
  address: "United Kingdom", // placeholder — replace with registered address
};

export const NAV_LINKS = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "AI Solutions", href: "#ai-solutions" },
  { label: "Industries", href: "#industries" },
  { label: "Portfolio", href: "#portfolio" },
  { label: "Pricing", href: "#pricing" },
  { label: "Contact", href: "#contact" },
];

export const VALUE_CARDS = [
  {
    icon: BrainCircuit,
    title: "AI-Powered",
    text: "Intelligent solutions that help businesses work smarter.",
  },
  {
    icon: Zap,
    title: "Automated",
    text: "Reduce repetitive manual work with connected workflows.",
  },
  {
    icon: HeartHandshake,
    title: "Customer-Focused",
    text: "Build better digital experiences for your customers.",
  },
  {
    icon: Scaling,
    title: "Scalable",
    text: "Technology designed to grow alongside your business.",
  },
];

export const MARQUEE_ITEMS = [
  "AI Automation",
  "WhatsApp AI Chatbots",
  "AI Voice Agents",
  "Website Development",
  "Mobile Apps",
  "Business Automation",
  "Custom Software",
  "Digital Marketing",
];

export const ABOUT_POINTS = [
  { icon: MapPin, label: "UK-registered company" },
  { icon: BrainCircuit, label: "AI-first approach" },
  { icon: Target, label: "Business-focused solutions" },
  { icon: ServerCog, label: "Custom development" },
  { icon: Workflow, label: "Automation expertise" },
  { icon: Globe, label: "UK & India market focus" },
];

export const SERVICE_GROUPS = [
  {
    id: "ai",
    overline: "AI Solutions",
    title: "Intelligence, built in",
    services: [
      {
        icon: Bot,
        title: "AI Chatbots",
        text: "Intelligent conversational assistants for websites and messaging platforms.",
      },
      {
        icon: MessageCircle,
        title: "WhatsApp AI Automation",
        text: "Automate customer enquiries, orders, bookings, support and follow-ups through WhatsApp.",
      },
      {
        icon: AudioLines,
        title: "AI Voice Agents",
        text: "AI-powered voice assistants for customer calls, enquiries, bookings and business processes.",
      },
      {
        icon: Workflow,
        title: "Business Automation",
        text: "Connect your business systems and automate repetitive workflows.",
      },
      {
        icon: Sparkles,
        title: "Custom AI Solutions",
        text: "Build AI solutions around specific business requirements.",
      },
    ],
  },
  {
    id: "development",
    overline: "Digital Development",
    title: "Engineered to perform",
    services: [
      {
        icon: Globe,
        title: "Website Development",
        text: "Premium responsive websites for businesses, brands and organisations.",
      },
      {
        icon: LayoutPanelLeft,
        title: "Web Application Development",
        text: "Custom web applications designed around business requirements.",
      },
      {
        icon: Smartphone,
        title: "Mobile App Development",
        text: "Modern mobile applications for customer-facing and internal business use.",
      },
      {
        icon: ServerCog,
        title: "Custom Software",
        text: "Purpose-built software solutions for specific operational requirements.",
      },
    ],
  },
  {
    id: "growth",
    overline: "Digital Growth",
    title: "Visibility that converts",
    services: [
      {
        icon: TrendingUp,
        title: "Digital Marketing",
        text: "Digital marketing solutions designed to improve online visibility and customer engagement.",
      },
      {
        icon: Share2,
        title: "Social Media Solutions",
        text: "Content and digital solutions to help businesses strengthen their online presence.",
      },
      {
        icon: RefreshCw,
        title: "Business Digital Transformation",
        text: "Help businesses replace manual processes with modern digital systems.",
      },
    ],
  },
];

export const FEATURED_AI = [
  {
    icon: MessageCircle,
    tag: "WhatsApp",
    title: "WhatsApp AI Customer Assistant",
    text: "Allow your business to automate the conversations that matter most.",
    features: [
      "Customer enquiries",
      "Product information",
      "Orders",
      "Bookings",
      "FAQs",
      "Customer support",
      "Payment workflows",
      "Follow-ups",
    ],
  },
  {
    icon: AudioLines,
    tag: "Voice",
    title: "AI Voice Agent",
    text: "An always-available voice for your business phone line.",
    features: [
      "Incoming calls",
      "Customer enquiries",
      "Appointment bookings",
      "Lead qualification",
      "Customer support",
      "Call routing",
    ],
  },
  {
    icon: Workflow,
    tag: "Automation",
    title: "Business Automation",
    text: "Connect your tools and let workflows run themselves.",
    features: [
      "Leads",
      "Orders",
      "CRM",
      "Emails",
      "WhatsApp",
      "Payments",
      "Notifications",
      "Internal workflows",
    ],
  },
];

export const PROCESS_STEPS = [
  {
    number: "01",
    title: "Discover",
    text: "We understand your business, challenges and goals.",
  },
  {
    number: "02",
    title: "Design",
    text: "We design the right technology solution around your requirements.",
  },
  {
    number: "03",
    title: "Build",
    text: "Our team develops and integrates your solution.",
  },
  {
    number: "04",
    title: "Grow",
    text: "We help you improve, optimise and scale your digital systems.",
  },
];

export const INDUSTRIES = [
  {
    icon: UtensilsCrossed,
    title: "Restaurants & Hospitality",
    text: "AI ordering, WhatsApp automation, bookings and customer communication.",
  },
  {
    icon: ShoppingBag,
    title: "Retail & E-commerce",
    text: "Customer support, product enquiries, order automation and digital experiences.",
  },
  {
    icon: Briefcase,
    title: "Professional Services",
    text: "Lead management, customer communication and workflow automation.",
  },
  {
    icon: Rocket,
    title: "Startups",
    text: "Websites, apps, AI solutions and scalable digital infrastructure.",
  },
  {
    icon: Building2,
    title: "Small & Medium Businesses",
    text: "Affordable automation and digital transformation solutions.",
  },
  {
    icon: Puzzle,
    title: "Other Businesses",
    text: "Custom technology solutions based on individual requirements.",
  },
];

export const BENEFITS = [
  {
    icon: Target,
    title: "Business-First Approach",
    text: "We focus on solving real business problems rather than simply adding technology.",
  },
  {
    icon: Puzzle,
    title: "Custom Solutions",
    text: "Every business is different, so solutions can be tailored to specific requirements.",
  },
  {
    icon: BrainCircuit,
    title: "AI & Automation Focus",
    text: "Use modern AI and automation technologies to reduce repetitive work.",
  },
  {
    icon: Cpu,
    title: "Modern Technology",
    text: "Build using modern, scalable technologies.",
  },
  {
    icon: Globe,
    title: "UK & India",
    text: "Serving businesses across the UK and India.",
  },
  {
    icon: Leaf,
    title: "Long-Term Partnership",
    text: "Provide ongoing support, improvements and technology guidance.",
  },
];

export const PORTFOLIO_CATEGORIES = [
  "All",
  "AI Automation",
  "WhatsApp Automation",
  "Website Development",
  "Business Automation",
  "Mobile Applications",
  "Digital Marketing",
];

export const PORTFOLIO_ITEMS = [
  {
    category: "AI Automation",
    title: "Intelligent Workflow Automation",
    status: "Selected Solution — Coming Soon",
  },
  {
    category: "WhatsApp Automation",
    title: "WhatsApp Customer Assistant",
    status: "Selected Solution — Coming Soon",
  },
  {
    category: "Website Development",
    title: "Premium Business Website",
    status: "Selected Solution — Coming Soon",
  },
  {
    category: "Business Automation",
    title: "Connected Operations Platform",
    status: "Selected Solution — Coming Soon",
  },
  {
    category: "Mobile Applications",
    title: "Customer-Facing Mobile App",
    status: "Selected Solution — Coming Soon",
  },
  {
    category: "Digital Marketing",
    title: "Digital Growth Programme",
    status: "Selected Solution — Coming Soon",
  },
];

export const PRICING_TIERS = [
  {
    name: "Starter",
    tagline: "For businesses beginning their digital transformation.",
    features: [
      "Business websites",
      "Basic automation",
      "Digital presence",
      "Initial AI solutions",
    ],
    cta: "Get Started",
    featured: false,
  },
  {
    name: "Growth",
    tagline: "For businesses looking to automate and scale.",
    features: [
      "AI chatbots",
      "WhatsApp automation",
      "Business workflows",
      "Advanced websites/apps",
    ],
    cta: "Talk To Us",
    featured: true,
  },
  {
    name: "Custom",
    tagline: "For businesses requiring tailored technology.",
    features: [
      "Custom AI",
      "AI voice agents",
      "Advanced automation",
      "Custom software",
      "Integrations",
    ],
    cta: "Request a Quote",
    featured: false,
  },
];

export const CONTACT_SERVICES = [
  "AI Chatbot",
  "WhatsApp Automation",
  "AI Voice Agent",
  "Business Automation",
  "Website Development",
  "Web Application",
  "Mobile App Development",
  "Digital Marketing",
  "Custom Software",
  "Other",
];

export const BUDGET_OPTIONS = [
  "Under £1,000",
  "£1,000 – £5,000",
  "£5,000 – £15,000",
  "£15,000+",
  "Not sure yet",
];

export const FAQS = [
  {
    q: "What services does Prata Technologies provide?",
    a: "We provide AI solutions (chatbots, WhatsApp automation, AI voice agents), business automation, website and web application development, mobile app development, digital marketing and custom software solutions.",
  },
  {
    q: "Can you build a custom AI chatbot for my business?",
    a: "Yes. We design and build AI chatbots around your specific business processes — from answering customer enquiries on your website to handling orders, bookings and support through messaging platforms.",
  },
  {
    q: "Can you automate my WhatsApp business?",
    a: "Yes. We build WhatsApp AI automation that can handle customer enquiries, orders, bookings, FAQs, payment workflows and follow-ups, so your customers get instant responses around the clock.",
  },
  {
    q: "Can you build an AI voice agent?",
    a: "Yes. Our AI voice agents can answer incoming calls, handle customer enquiries, book appointments, qualify leads and route calls — designed around the way your business operates.",
  },
  {
    q: "Do you develop websites and mobile apps?",
    a: "Yes. We develop premium responsive websites, custom web applications and modern mobile apps for both customer-facing and internal business use.",
  },
  {
    q: "Do you work with businesses outside the UK?",
    a: "Yes. Alongside the UK, we work with businesses in India and can support clients internationally depending on the project requirements.",
  },
  {
    q: "Do you provide custom automation solutions?",
    a: "Yes. We connect your existing tools — CRM, email, WhatsApp, payments and more — and automate the repetitive workflows in between, tailored to your operations.",
  },
  {
    q: "How do I get a quotation?",
    a: "Simply send an enquiry through our contact form or book a free consultation. Every project is different, so we will review your requirements and provide a tailored proposal.",
  },
];

export const SOCIAL_PLACEHOLDERS = ["LinkedIn", "X", "Instagram", "Facebook"];
